import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { mixed, number, object, string } from 'yup';
import { NJButton, NJFormItem, NJHeading, NJText } from '@engie-group/fluid-design-system-react';
import { UMSelect } from '../../urbanmap/_components';
import type { Project } from './types';

const schema = object({
  name: string().trim().required('Nom requis'), perimeter: string().trim().required('Périmètre requis'),
  extent: string().required('Emprise requise').test('bounds', 'Format : ouest,sud,est,nord ; emprise WGS84 valide.', value => {
    const v = value.split(',').map(Number);
    return v.length === 4 && v.every(Number.isFinite) && v[0] >= -180 && v[2] <= 180 &&
      v[1] >= -90 && v[3] <= 90 && v[0] < v[2] && v[1] < v[3];
  }),
  station: string().trim().required('Station requise'),
  dju: number().typeError('Nombre requis').positive('Valeur positive requise').required(),
  heatRatio: number().typeError('Nombre requis').positive('Valeur positive requise').required(),
  coldRatio: number().typeError('Nombre requis').positive('Valeur positive requise').required(),
  networkKind: mixed<'existing' | 'new'>().oneOf(['existing', 'new']).required(),
  revision: number().required(),
});
export function ProjectSettings({ project, save }: { project: Project; save: (project: Project) => void }) {
  const { control, handleSubmit, watch, setValue, reset, formState: { errors, isSubmitSuccessful, isDirty } } =
    useForm<Project>({ defaultValues: project, resolver: yupResolver(schema) });
  return <section className="sp-settings">
    <NJHeading as="h2" scale="lg">Paramètres du projet</NJHeading>
    <NJText>Station météo, DJU et ratios de consommation : toute modification signale les plans calculés comme à recalculer.</NJText>
    <form onSubmit={handleSubmit(values => { save(values); reset(values); })} className="sp-stack">
      {([
        ['name', 'Nom du projet', 'text'], ['perimeter', 'Nom du périmètre', 'text'],
        ['extent', 'Emprise géographique · ouest,sud,est,nord', 'text'],
        ['station', 'Station météo', 'text'], ['dju', 'DJU', 'number'],
        ['heatRatio', 'Ratio de consommation · chaud', 'number'], ['coldRatio', 'Ratio de consommation · froid', 'number'],
      ] as const).map(([key, label, type]) => <Controller key={key} name={key} control={control} render={({ field }) =>
        <NJFormItem id={key} label={label} type={type} step={type === 'number' ? 'any' : undefined}
          value={Number.isNaN(field.value) ? '' : String(field.value)} onBlur={field.onBlur}
          onChange={e => {
            if (e.target instanceof HTMLInputElement) field.onChange(type === 'number' ? e.target.valueAsNumber : e.target.value);
          }}
          hasError={!!errors[key]} subscriptMessage={errors[key]?.message} />} />)}
      <NJText as="div">Nature du réseau</NJText>
      <UMSelect ariaLabel="Nature du réseau" value={watch('networkKind')} onChange={v => {
        if (v === 'new' || v === 'existing') setValue('networkKind', v, { shouldDirty: true });
      }} options={[{ value: 'existing', label: 'Réseau existant' }, { value: 'new', label: 'Réseau nouveau' }]} />
      <NJText scale="sm">Le nom, le périmètre et la nature du réseau ne déclenchent pas de recalcul. Réseau nouveau : seuls les départs de centrales sont utilisés.</NJText>
      <NJButton label="Enregistrer les paramètres" type="submit" disabled={!isDirty} />
      {isSubmitSuccessful && !isDirty && <NJText as="div" role="status">Paramètres enregistrés.</NJText>}
    </form>
  </section>;
}
